import { type ReactNode, useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/core/lib/cn';

type ModalSize = 'md' | 'lg' | 'xl';
const SIZES: Record<ModalSize, string> = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'lg',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      {/* Flex column so the header/footer stay fixed and the body scrolls — tall
          modals never exceed the viewport. */}
      <div
        className={cn('flex max-h-[90vh] w-full flex-col rounded-panel bg-white shadow-xl', SIZES[size])}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-fv-border px-5 py-4">
          <h2 className="text-lg font-bold text-fv-text">{title}</h2>
          <button onClick={onClose} aria-label="Close" className="rounded text-fv-muted transition-colors hover:text-fv-text">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="flex shrink-0 justify-end gap-2 border-t border-fv-border px-5 py-3">{footer}</div>
        )}
      </div>
    </div>
  );
}
